import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PercentageCircle from '../components/PercentageCircle';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/format';

const OverviewPage = () => {
  const [gatewayCounts, setGatewayCounts] = useState({ total: 0, lost: 0 });
  const [waterMeterCounts, setWaterMeterCounts] = useState({
    total: 0,
    unhealthy: 0,
    lost: 0,
    critical: 0,
  });
  const [gatewayActivities, setGatewayActivities] = useState([]);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // AbortController refs
  const countsAbortControllerRef = useRef();
  const activitiesAbortControllerRef = useRef();

  const fetchCounts = async () => {
    // create and store a new controller
    const controller = new AbortController();
    countsAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const [
        gwTotalRes,
        gwLostRes,
        wmTotalRes,
        wmUnhealthyRes,
        wmLostRes,
        wmCriticalRes,
      ] = await Promise.all([
        api.get('/api/proxy/gateways/count', { signal }),
        api.get('/api/proxy/gateways/lost/count', { signal }),
        api.get('/api/proxy/wmeters/count', { signal }),
        api.get('/api/proxy/wmeters/unhealthy/count', { signal }),
        api.get('/api/proxy/wmeters/lost/count', { signal }),
        api.get('/api/proxy/wmeters/critical/count', { signal }),
      ]);
      setGatewayCounts({
        total: gwTotalRes.data.count,
        lost: gwLostRes.data.count,
      });
      setWaterMeterCounts({
        total: wmTotalRes.data.count,
        unhealthy: wmUnhealthyRes.data.count,
        lost: wmLostRes.data.count,
        critical: wmCriticalRes.data.count,
      });
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
      }
    }
  };

  const fetchGatewayActivities = async () => {
    // create and store a new controller
    const controller = new AbortController();
    activitiesAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const res = await api.get('/api/proxy/gateways/gateway-activity', {
        params: { max: 50 },
        signal,
      });
      setGatewayActivities(res.data);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
      }
    }
  };

  useEffect(() => {
    fetchCounts();
    fetchGatewayActivities();
    const interval = setInterval(() => {
      fetchCounts();
      fetchGatewayActivities();
    }, 30000);

    return () => {
      // abort any in-flight requests
      countsAbortControllerRef.current?.abort();
      activitiesAbortControllerRef.current?.abort();
      clearInterval(interval);
    };
  }, []);

  const healthyGateways = gatewayCounts.total - gatewayCounts.lost;
  const healthyWaterMeters = waterMeterCounts.total - waterMeterCounts.unhealthy;

  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        flexGrow: 1,
      }}
    >
      {/* Top Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={6} md={6}>
            <Paper
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #ddd',
                boxShadow: 'none',
                p: 0.5,
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 250 }}>
                <PercentageCircle
                  percentage={
                    gatewayCounts.total
                      ? (healthyGateways / gatewayCounts.total) * 100
                      : 0
                  }
                  label={healthyGateways}
                  color="#27ae60"
                  onClick={() => navigate('/gateways')}
                />
              </Box>
              <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                Healthy Gateways
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={6}>
            <Paper
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #ddd',
                boxShadow: 'none',
                p: 0.5,
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 250 }}>
                <PercentageCircle
                  percentage={
                    waterMeterCounts.total
                      ? (healthyWaterMeters / waterMeterCounts.total) * 100
                      : 0
                  }
                  label={healthyWaterMeters}
                  color="#27ae60"
                  onClick={() => navigate('/water-meters')}
                />
              </Box>
              <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                Healthy Meters
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={4} sm={4} md={4}>
            <Paper
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #ddd',
                boxShadow: 'none',
                p: 0.5,
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 220 }}>
                <PercentageCircle
                  percentage={100}
                  label={gatewayCounts.lost}
                  color="#808080"
                  onClick={() => navigate('/lost-gateways')}
                />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{ mt: 0.5, textAlign: 'center' }}
              >
                Lost Gateways
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4} sm={4} md={4}>
            <Paper
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #ddd',
                boxShadow: 'none',
                p: 0.5,
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 220 }}>
                <PercentageCircle
                  percentage={100}
                  label={waterMeterCounts.lost}
                  color="#808080"
                  onClick={() => navigate('/lost-water-meters')}
                />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{ mt: 0.5, textAlign: 'center' }}
              >
                Lost Meters
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4} sm={4} md={4}>
            <Paper
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #ddd',
                boxShadow: 'none',
                p: 0.5,
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 220 }}>
                <PercentageCircle
                  percentage={100}
                  label={waterMeterCounts.critical}
                  color="#e74c3c"
                  onClick={() => navigate('/criticals')}
                />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{ mt: 0.5, textAlign: 'center' }}
              >
                Critical Meters
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Bottom Section: Activities List */}
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom>
          Gateways Activities
        </Typography>
        <Paper sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
          <Grid container sx={{ fontWeight: 'bold', mb: 1 }}>
            <Grid item xs={2}>
              <Typography noWrap sx={{ maxWidth: 'auto' }}>Gateway</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography>Meter</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography>Topic</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography>Action</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography noWrap sx={{ maxWidth: 'auto' }}>Timestamp</Typography>
            </Grid>
          </Grid>

          {gatewayActivities.map((activity, index) => (
            <Grid
              container
              key={index}
              sx={{ mb: 0.5, cursor: 'pointer' }}
              onClick={() => navigate(`/gateways/${activity.gatewayId}`)}
            >
              <Grid item xs={2}>
                <Typography noWrap sx={{ maxWidth: 'auto' }}>
                  {activity.gatewayId}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography noWrap sx={{ maxWidth: 'auto' }}>
                  {activity.meterId || 'NA'}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography noWrap sx={{ maxWidth: 'auto' }}>
                  {activity.mqttTopic?.slice(0, 20) || 'NA'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography noWrap sx={{ maxWidth: 'auto' }}>
                  {activity.action?.slice(0, 25) || activity.action}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography>{formatDate(activity.timestamp)}</Typography>
              </Grid>
            </Grid>
          ))}
        </Paper>
      </Box>
    </Box>
  );
};

export default OverviewPage;
